/**
 * Shared date formatting for the realtor tables.
 *
 * Replaces the old per-table helper, which printed the US `M/D/YYYY` order and
 * added a day (`getUTCDate() + 1`), so every row showed tomorrow's date.
 *
 * Values are not consistent across tables: objects store epoch milliseconds,
 * while a client's `dateFrom` comes straight from the date picker as text.
 * This handles both and passes anything unrecognisable through untouched.
 */

function pad(value) {
  return value < 10 ? "0" + value : "" + value;
}

export function formatTableDate(value) {
  // 0 means "no date recorded", not 1 Jan 1970.
  if (value === undefined || value === null || value === "" || value === 0) return "";

  var date;
  if (typeof value === "number") {
    date = new Date(value);
  } else if (/^\d{10,}$/.test(String(value).trim())) {
    // epoch milliseconds that arrived as a string
    date = new Date(Number(value));
  } else {
    date = new Date(value);
  }

  if (!date || isNaN(date.getTime())) return String(value);

  return pad(date.getDate()) + "." + pad(date.getMonth() + 1) + "." + date.getFullYear();
}

/**
 * Midnight, local time, on the day this value names — as epoch milliseconds.
 *
 * Replaces the shape that was copied into every save handler:
 *
 *     new Date(`${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getFullYear()}`)
 *
 * which reads the month and day in UTC, the year locally, and re-parses the
 * result as local. In Kyiv (UTC+2/+3) the UTC day is the previous one until
 * 02:00 or 03:00, so anything saved in those hours was dated a day early. Some
 * copies carried a `+ 1` on the day to compensate — which made them a day LATE
 * for the other twenty-one hours, since the picker hands back the chosen day at
 * the current time of day, not at midnight.
 *
 * Local getters have no such window: they name the day the person is looking at.
 */
export function dayStart(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return NaN;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ).getTime();
}

/** Midnight local time today, as epoch milliseconds. */
export function startOfToday() {
  return dayStart(new Date());
}

/**
 * The `M/D/YYYY` string a client's `dateFrom` has always been stored as.
 *
 * The order is kept deliberately: rows saved by the old code are already in the
 * database in this shape and `formatTableDate` reads them back through
 * `new Date(string)`. Only the arithmetic is fixed — the day is now the one
 * that was picked.
 */
export function dayString(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return "";
  return (
    date.getMonth() + 1 + "/" + date.getDate() + "/" + date.getFullYear()
  );
}

export default formatTableDate;
